import { Link } from 'react-router-dom';
import { useSession } from '@/app/session';
import { fundingApplyPath } from '@/features/publicsite/catalog/fundingApplyPath';
import {
  MEASURE_STATUS,
  measureStatusClass,
  measureStatusKey,
} from '@/features/publicsite/catalog/measureStatus';
import { itemTextLang } from '@/features/publicsite/contentLanguage';
import type { FundingCardProps } from '@/features/publicsite/types/funding/FundingCardProps';
import type { MeasureTranslatedField } from '@/features/publicsite/types/funding/MeasureTranslatedField';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import type { Language } from '@/i18n/types';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * One measure in the /finantare grid.
 *
 * Not CatalogCard: that one is a badge, a heading, a muted subtitle and free lines, which is what
 * the other four catalogues need. This card has a shape of its own — the badge sits opposite the
 * title rather than above it, the domain and the support level share one line, the description is
 * clamped so every card in a row is the same height, and a footer rule separates the one fact a
 * reader is scanning for (how long is left) from the description above it.
 *
 * A field the measure has none of renders nothing rather than an empty label — the description and
 * the deadline are each optional in their own right.
 */
export function FundingCard({ measure, domain }: FundingCardProps) {
  const { t, language } = useTranslation(publicCatalog);
  const { user } = useSession();
  // Text by text: a measure translated except for its description shows that one line in Romanian
  // on the English page, and only that line carries `lang` (T234).
  const langOf = (field: MeasureTranslatedField) => itemTextLang(measure, field, language);

  // An OPEN call is the only one with somewhere to go: the application flow lives in the cabinet,
  // and there is no public page for a measure. A reader who may not file has nowhere to go either,
  // since that flow is gated on applications.create:own — see fundingApplyPath, which decides both.
  // A card that leads nowhere is a <div> and carries no arrow — an arrow that does nothing is
  // worse than no arrow.
  const to = fundingApplyPath(measure, user?.permissions ?? null);

  // Each part keeps its own language: the domain is the page's word for a nomenclature item, the
  // support level is the measure's own text and can still be Romanian on the English card.
  const meta: { key: string; text: string; lang?: Language }[] = [];
  if (domain) meta.push({ key: 'domain', text: t('fundingDomain', { domain }) });
  if (measure.supportLevel) {
    meta.push({
      key: 'supportLevel',
      text: t('fundingSupportLevel', { level: measure.supportLevel }),
      lang: langOf('supportLevel'),
    });
  }

  const footer = measure.closesAt
    ? t('fundingDeadline', { date: measure.closesAt })
    : measure.status === MEASURE_STATUS.closed
      ? t('fundingSessionOver')
      : null;

  const body = (
    <>
      <span className="funding-card__head">
        <span className={measureStatusClass(measure.status)}>
          {t(measureStatusKey(measure.status))}
        </span>
      </span>
      <h3 className="funding-card__title" lang={langOf('title')}>
        {measure.title}
      </h3>
      {meta.length > 0 && (
        <p className="funding-card__meta">
          {meta.map((part, index) => (
            <span key={part.key}>
              {index > 0 && ' · '}
              <span lang={part.lang}>{part.text}</span>
            </span>
          ))}
        </p>
      )}
      {measure.description && (
        <p className="funding-card__desc" lang={langOf('description')}>
          {measure.description}
        </p>
      )}
      {/* Always drawn, even empty: margin-top: auto keeps it at the bottom of every card in a row,
        which lines a deadline up across a row once one exists. The call-to-action is a labelled
        word here, not the icon-only arrow this replaced — an OPEN card with no visible "Depune
        cererea" looked identical to a CLOSED one but for a small status tag, and a whole clickable
        card with no readable label anywhere on it is discoverable only by accident. */}
      <span className="funding-card__footer">
        <span>{footer}</span>
        {to && <span className="funding-card__cta">{t('fundingApply')}</span>}
      </span>
    </>
  );

  return to ? (
    <Link className="card funding-card" id={`masura-${measure.id}`} to={to}>
      {body}
    </Link>
  ) : (
    // tabIndex, so a fragment link to #masura-{id} moves the caret and not only the viewport —
    // the same reason CatalogCard carries one. -1 keeps a non-link card out of the tab order.
    <div className="card funding-card" id={`masura-${measure.id}`} tabIndex={-1}>
      {body}
    </div>
  );
}
